import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';

import {
  apiClient,
  applySession,
  clearSession,
  onSessionChange,
  refreshAuth,
  type SessionUser,
} from '../../lib/api-client';

export type AuthUser = SessionUser;

interface AuthContextValue {
  user: AuthUser | null;
  loading: boolean;
  login: (input: LoginInput) => Promise<LoginResult>;
  loginWith2fa: (challengeToken: string, code: string) => Promise<void>;
  confirmInitial2fa: (setupToken: string, code: string) => Promise<void>;
  logout: () => Promise<void>;
  refreshSession: () => Promise<void>;
}

interface LoginInput {
  email: string;
  password: string;
}

export interface TwoFactorSetupChallenge {
  setupToken: string;
  otpauthUrl: string;
  qrCodeDataUrl: string;
  secret: string;
  email: string;
}

export type LoginResult =
  | { kind: 'success' }
  | { kind: 'requires2fa'; challengeToken: string }
  | { kind: 'requiresSetup'; setup: TwoFactorSetupChallenge };

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [loading, setLoading] = useState(true);

  // Au montage : s'abonne aux changements de session (refresh par l'intercepteur,
  // autre onglet, déconnexion), puis tente une ré-authentification via le cookie.
  useEffect(() => {
    onSessionChange((session) => setUser(session?.user ?? null));
    void refreshAuth()
      .then((session) => setUser(session?.user ?? null))
      .finally(() => setLoading(false));
    return () => onSessionChange(null);
  }, []);

  const refreshSession = useCallback(async () => {
    const session = await refreshAuth();
    setUser(session?.user ?? null);
  }, []);

  const login = useCallback(async (input: LoginInput): Promise<LoginResult> => {
    const res = await apiClient.post<
      | { accessToken: string; user: AuthUser }
      | { requires2fa: true; challengeToken: string }
      | {
          requiresSetup: true;
          setupToken: string;
          otpauthUrl: string;
          qrCodeDataUrl: string;
          secret: string;
          email: string;
        }
    >('/auth/login', input);
    if ('requiresSetup' in res.data && res.data.requiresSetup) {
      return {
        kind: 'requiresSetup',
        setup: {
          setupToken: res.data.setupToken,
          otpauthUrl: res.data.otpauthUrl,
          qrCodeDataUrl: res.data.qrCodeDataUrl,
          secret: res.data.secret,
          email: res.data.email,
        },
      };
    }
    if ('requires2fa' in res.data && res.data.requires2fa) {
      return { kind: 'requires2fa', challengeToken: res.data.challengeToken };
    }
    if ('accessToken' in res.data) {
      applySession({ accessToken: res.data.accessToken, user: res.data.user });
      setUser(res.data.user);
    }
    return { kind: 'success' };
  }, []);

  const loginWith2fa = useCallback(async (challengeToken: string, code: string): Promise<void> => {
    const res = await apiClient.post<{ accessToken: string; user: AuthUser }>('/auth/login/2fa', {
      challengeToken,
      code,
    });
    applySession({ accessToken: res.data.accessToken, user: res.data.user });
    setUser(res.data.user);
  }, []);

  const confirmInitial2fa = useCallback(async (setupToken: string, code: string): Promise<void> => {
    const res = await apiClient.post<{ accessToken: string; user: AuthUser }>(
      '/auth/login/2fa-confirm',
      { setupToken, code },
    );
    applySession({ accessToken: res.data.accessToken, user: res.data.user });
    setUser(res.data.user);
  }, []);

  const logout = useCallback(async () => {
    try {
      await apiClient.post('/auth/logout');
    } finally {
      clearSession();
      setUser(null);
    }
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({ user, loading, login, loginWith2fa, confirmInitial2fa, logout, refreshSession }),
    [user, loading, login, loginWith2fa, confirmInitial2fa, logout, refreshSession],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within <AuthProvider>');
  return ctx;
}
